
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import AnimatedPage from "@/components/AnimatedPage";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getDashboardData } from "@/lib/api";
import { InfoIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// Import the new dashboard components
import { DashboardCards } from "@/components/dashboard/DashboardCards";
import OverviewContent from "@/components/dashboard/OverviewContent";
import PaymentsList from "@/components/dashboard/PaymentsList";
import {
  calculateUniqueCustomers,
  prepareChitPoolData,
  prepareLoanData,
  preparePieChartData,
  preparePortfolioValueData,
  formatCurrency,
  generateInterestData
} from "@/components/dashboard/DashboardUtils";

const Home = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["dashboardData"],
    queryFn: getDashboardData
  });

  const [activeTab, setActiveTab] = useState("overview");

  if (isLoading) {
    return (
      <AnimatedPage>
        <div className="container py-8">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              <p className="text-muted-foreground">Loading dashboard data...</p>
            </div>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  if (isError) {
    return (
      <AnimatedPage>
        <div className="container py-8">
          <Alert variant="destructive">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : "Failed to load dashboard data. Please try refreshing the page."}
            </AlertDescription>
          </Alert>
        </div>
      </AnimatedPage>
    );
  }

  const hasChitPoolData = data?.chitPools && data.chitPools.length > 0;
  const hasLoanData = data?.loans && data.loans.length > 0;

  // Calculate total members (unique customers)
  const totalUniqueCustomers = calculateUniqueCustomers(
    hasChitPoolData, 
    hasLoanData, 
    data?.chitPools, 
    data?.loans
  );

  // Calculate total value (sum of all chit pools and loans)
  const totalChitPoolValue = hasChitPoolData ? 
    data.chitPools.reduce((sum, pool) => sum + pool.amount, 0) : 0;
    
  const totalLoanValue = hasLoanData ? 
    data.loans.reduce((sum, loan) => sum + loan.amount, 0) : 0;
    
  const totalValue = totalChitPoolValue + totalLoanValue;

  // Prepare data for charts
  const chitPoolData = prepareChitPoolData(hasChitPoolData, data?.chitPools);
  const loanData = prepareLoanData(hasLoanData, data?.loans);
  const pieChartData = preparePieChartData(data?.chitPoolsCount, data?.loansCount);
  const portfolioValueData = preparePortfolioValueData(totalChitPoolValue, totalLoanValue);
  const interestData = generateInterestData(hasLoanData, loanData);

  return (
    <AnimatedPage>
      <div className="container py-8">
        <div className="grid gap-6">
          <div className="flex flex-col sm:flex-row justify-between items-start mb-6">
            <div className="text-left w-full">
              <h1 className="text-3xl font-bold tracking-tight text-left">Dashboard</h1>
              <p className="text-muted-foreground text-left">
                Overview of your chit pools and loans
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex gap-2">
              <Button asChild>
                <Link to="/chit-pools/new">Create Chit Pool</Link>
              </Button>
              <Button asChild>
                <Link to="/loans/new">Create Loan</Link>
              </Button>
            </div>
          </div>

          <DashboardCards 
            data={data}
            totalUniqueCustomers={totalUniqueCustomers}
            totalValue={totalValue}
            totalChitPoolValue={totalChitPoolValue}
            totalInterestCollected={data?.totalInterestCollected || 0}
          />

          <Tabs defaultValue="overview" className="w-full" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="chitPayments">Chit Payments</TabsTrigger>
              <TabsTrigger value="interestPayments">Interest Payments</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="pt-4">
              <OverviewContent 
                chitPoolData={chitPoolData}
                loanData={loanData}
                interestData={interestData}
                pieChartData={pieChartData}
                portfolioValueData={portfolioValueData}
                formatCurrency={formatCurrency}
              />
            </TabsContent>
            
            <TabsContent value="chitPayments" className="pt-4">
              <PaymentsList 
                title="Upcoming Chit Payments"
                description="Payments due in the next 30 days"
                payments={data?.upcomingChitPayments || []}
                type="chit"
              />
            </TabsContent>
            
            <TabsContent value="interestPayments" className="pt-4">
              <PaymentsList 
                title="Upcoming Interest Payments"
                description="Interest payments due in the next 30 days"
                payments={data?.upcomingInterestPayments || []}
                type="interest"
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AnimatedPage>
  );
};

export default Home;
